import type { InputTable } from '@blocksuite/affine/blocks/streamlain';
import type { FilterGroup } from '@blocksuite/affine/data-view';
import { Service } from '@toeverything/infra';
import { LiveData } from '@toeverything/infra';

// sl-trigger-removal.5: Import from types instead of removed trigger service
import type { TriggerConfig } from '../types';
import { DEFAULT_TRIGGER_CONFIG } from '../types';
import type { BatchConfig } from './execution';
import type { WorkflowStepSummary } from './workflow';

// =============================================================================
// Status Tracking Types (T044, E018-S004-T001)
// =============================================================================

/**
 * Standard status values for select column options
 */
export const STATUS_STANDARD_VALUES = {
  success: ['Done', 'Processed', 'Completed'],
  error: ['Error', 'Failed'],
} as const;

/**
 * Status tracking configuration for post-processing.
 *
 * sl-8fwi.11: `mode` field removed — only status_column tracking exists.
 */
export interface StatusTrackingConfig {
  /** Whether status tracking is enabled */
  enabled: boolean;
  /** Column ID to update (null = create new column) */
  columnId: string | null;
  /** Configuration for creating a new column */
  createColumn?: { name: string };
  /** Standard options for the select column */
  standardOptions: string[];
  /** Value to set on successful processing */
  successValue: string;
  /** Value to set on error (optional) */
  errorValue?: string;
}

/**
 * Default status tracking config (disabled, status_column mode)
 */
export const DEFAULT_STATUS_TRACKING_CONFIG: StatusTrackingConfig = {
  enabled: false,
  columnId: null,
  createColumn: { name: 'Status' },
  standardOptions: ['Pending', 'Done', 'Error'],
  successValue: 'Done',
  errorValue: 'Error',
};

/**
 * E002-T016: AutomationService
 *
 * State Management für Automation Blocks.
 * Verwaltet aktiven Block und Konfiguration.
 */

/**
 * S020-8.20.2: Relation match mode for Smart Relation Lookup
 */
export type RelationMatchMode = 'exact' | 'contains' | 'starts_with';

/**
 * Behavior when multiple rows match during update_by_match/upsert.
 */
export type MultiMatchBehavior =
  | 'fail' // Throw error (default, safest)
  | 'update_all' // Update all matching rows
  | 'update_first' // Update only the first match
  | 'skip' // Skip row, log warning
  | 'ask'; // Create PendingDecision for user resolution

/**
 * Multi-Output: A target table for writing automation results.
 */
export interface OutputTable {
  /** Block ID of the target table */
  tableId: string;
  /** Display name of the table */
  tableName: string;
  /** Doc ID where the table resides (for CrossDoc support) */
  docId: string;
  /** sl-trm: Output mode for this specific target (overrides global) */
  mode?: AutomationOutputMode;
  /** sl-mau5: Reference to MatchConfigProps entry for row matching */
  matchConfigId?: string;
  /** Stable alias exposed as __targets.<alias> for later output mappings */
  targetAlias?: string;
  /** Response array path; when set, writes one target row per item */
  sourceArrayField?: string;
}

/**
 * Output field mapping: maps a response field to a target column
 * Multi-Output: Each mapping specifies which table (from outputTables) to write to.
 *
 * sl-mau5: Match config is now a first-class object in matchConfigs[].
 * OutputMapping only stores the reference via matchConfigId.
 */
export interface OutputMapping {
  /** Which output table this mapping writes to (from outputTables) */
  tableId: string;
  /** Field path in workflow engine response (e.g. "enriched_name", "data.score") — empty when fixedValue set */
  responseField: string;
  /** Target column ID */
  targetColumnId: string;
  /** sl-mau5: Reference to MatchConfigProps entry for relation lookup */
  matchConfigId?: string;
  /** sl-vb5r: Fixed value (option ID) for select columns — mutually exclusive with responseField */
  fixedValue?: string;
  /**
   * sl-g3on.4.1: Human-readable name of the workflow output field that
   * `responseField` references. Optional and additive — historical mappings
   * without this field still resolve to `responseField` (the raw field ID)
   * at render time. The Y.js writer in sl-g3on.4.2 fills it in from the
   * workflow output schema; the canvas block reads it via
   * `responseFieldName ?? responseField`.
   */
  responseFieldName?: string;
  /**
   * Type of the workflow output field (e.g. "text", "number"). Persisted
   * so the canvas block can render a type pill without re-fetching the
   * workflow schema.
   */
  responseFieldType?: string;
}

/**
 * sl-mau5: First-class match configuration for row matching and relation lookup.
 *
 * Replaces 7 scattered fields that were on OutputMapping.
 * Referenced by OutputTable.matchConfigId (row matching) and
 * OutputMapping.matchConfigId (relation lookup).
 */
/** sl-tah4: Behavior when inverse relation steal conflict occurs */
export type StealBehavior = 'silent_steal' | 'fail' | 'ask';

export interface MatchConfigProps {
  /** Client-generated stable ID (nanoid) */
  id: string;
  /** Target table ID to search in */
  targetTableId: string;
  /** Behavior when multiple rows match */
  multiMatchBehavior: MultiMatchBehavior;
  /** Behavior when no match found (only for row matching, not relation lookup) */
  notFoundBehavior?: 'fail' | 'create';
  /** sl-tah4: Behavior when inverse relation steal conflict occurs (default: 'fail') */
  stealBehavior?: StealBehavior;
  /** Match keys: which columns to search and how */
  keys: Array<{
    /** Column ID in the target table */
    columnId: string;
    /** Field from webhook response to use as search value */
    sourceField: string;
    /** How to match: auto | exact | contains | starts_with */
    matchMode: string;
  }>;
}

/**
 * E007-T022: Input field mapping: maps a workflow schema field to a source column.
 * Canonical definition lives in `@streamlain/field-types`.
 */
import type { InputMapping } from '@streamlain/field-types';
export type { InputMapping };

export interface AutomationConfig {
  /** Automation title (defaults to workflow name when workflow is selected) */
  title?: string | null;
  /** Whether the title follows the selected workflow or belongs to the user. */
  titleMode?: 'auto' | 'custom';
  /** Source Table ID */
  sourceTableId: string | null;
  /** Source Table Name (for display) */
  sourceTableName?: string | null;
  /** E018-S008-T005: Source Doc ID (for CrossDoc indicator) */
  sourceDocId?: string | null;
  /** Historical single-output target table. Multi-output configs use outputTables. */
  targetTableId?: string | null;
  /** Historical single-output target doc. Multi-output configs use outputTables. */
  targetDocId?: string | null;
  /** Data-View compatible filter group */
  filterGroup: FilterGroup;
  /** Selected column IDs to include in payload when no schema is configured. */
  selectedColumns: string[];
  /** E007-T022: Schema-based input mappings (takes precedence over selectedColumns) */
  inputMappings?: InputMapping[];
  /** sl-m379: Multi-Input — lead table + secondary lookup tables */
  inputTables?: InputTable[];
  /** Workflow ID (workflow engine workflow) */
  workflowId: string | null;
  /** Name of the selected workflow (for display when no custom name is set) */
  workflowName?: string | null;
  /** Ordered workflow-step projection persisted on the block for local-first canvas rendering. */
  workflowSteps?: WorkflowStepSummary[];
  /** Workflow ID the persisted step projection belongs to (empty arrays included). */
  workflowStepsSourceId?: string | null;
  /** Manual webhook URL override (takes precedence over workflow's webhookUrl) */
  webhookUrlOverride: string | null;
  /** Multi-Output: List of target tables for writing results (each has its own mode) */
  outputTables: OutputTable[];
  /** Output field mappings */
  outputMappings: OutputMapping[];
  /** Batching & Error Handling config (T037) */
  batchConfig?: BatchConfig;
  /** Trigger config (T036) */
  triggerConfig?: TriggerConfig;
  /** Status tracking config (T044) */
  statusTracking?: StatusTrackingConfig;
  /** sl-3nk: Owner user ID (for permission checks) */
  createdBy?: string | null;
  /** sl-mau5: First-class match configurations (replaces scattered fields on OutputMapping) */
  matchConfigs?: MatchConfigProps[];
}

/**
 * sl-trm: Output modes for automation results
 * - update_source: Update the source row that triggered the automation
 * - append: Create new rows in the target table
 * - update_by_match: Find existing row by match key and update it
 * - upsert: Find existing row by match key or create new one
 */
export type AutomationOutputMode =
  | 'update_source'
  | 'append'
  | 'append_each'
  | 'update_by_match'
  | 'upsert';

/**
 * sl-trm: Match mode for row lookup operations.
 * Controls how values are matched when finding rows for update_by_match/upsert.
 */
export type TargetMatchMode =
  | 'exact' // WHERE column = 'value' (case-insensitive)
  | 'contains' // WHERE column ILIKE '%value%'
  | 'starts_with' // WHERE column ILIKE 'value%'
  // | 'ai' // AI matching disabled -- pipeline refactoring needed
  | 'auto'; // Priority chain: exact -> contains -> ambiguous

/** Default empty filter group */
const emptyFilterGroup: FilterGroup = {
  type: 'group',
  op: 'and',
  conditions: [],
};

const defaultConfig: AutomationConfig = {
  sourceTableId: null,
  filterGroup: { ...emptyFilterGroup },
  selectedColumns: [],
  workflowId: null,
  webhookUrlOverride: null,
  outputTables: [],
  outputMappings: [],
  triggerConfig: { ...DEFAULT_TRIGGER_CONFIG },
  statusTracking: { ...DEFAULT_STATUS_TRACKING_CONFIG },
};

/**
 * sl-67kc.3.4: Deep-link target published by the workspace operations
 * chip when the user clicks a flyout row. The block-scoped History panel
 * reads this via {@link AutomationService.pendingHistoryRowTarget$},
 * expands + scrolls the matching row once it mounts, and clears the
 * target. LiveData replaces the earlier CustomEvent scroll dispatch so
 * the deep-link survives the first-mount race that events couldn't.
 */
export interface PendingHistoryRowTarget {
  blockId: string;
  executionLogId: string;
}

export class AutomationService extends Service {
  /**
   * sl-6nl: Storage key for persisting active block ID
   */
  private static readonly ACTIVE_BLOCK_STORAGE_KEY =
    'streamlain:automation:activeBlockId';

  /**
   * Aktuell ausgewählter Block für Sidebar
   * sl-6nl: Initialize from localStorage for persistence across sessions
   */
  readonly activeBlockId$ = new LiveData<string | null>(
    typeof window !== 'undefined'
      ? localStorage.getItem(AutomationService.ACTIVE_BLOCK_STORAGE_KEY)
      : null
  );

  /**
   * sl-67kc.3.4: Target row the History panel should scroll to on next
   * mount (or re-render) for the matching block. Set by the workspace
   * operations chip deep-link handler, consumed + cleared by the
   * History panel via {@link consumePendingHistoryRowTarget}.
   *
   * Not persisted — a deep-link that races a reload is expected to be
   * re-issued by the chip on the new session rather than replayed from
   * localStorage.
   */
  readonly pendingHistoryRowTarget$ =
    new LiveData<PendingHistoryRowTarget | null>(null);

  /**
   * Konfigurationen pro Block (in-memory cache)
   * In Produktion: Aus Block-Model laden
   */
  private readonly configs = new Map<string, LiveData<AutomationConfig>>();

  /**
   * Setzt den aktiven Block (für Sidebar)
   * sl-6nl: Persist to localStorage
   */
  setActiveBlock(blockId: string | null): void {
    this.activeBlockId$.next(blockId);
    // sl-6nl: Persist to localStorage
    if (typeof window !== 'undefined') {
      if (blockId) {
        localStorage.setItem(
          AutomationService.ACTIVE_BLOCK_STORAGE_KEY,
          blockId
        );
      } else {
        localStorage.removeItem(AutomationService.ACTIVE_BLOCK_STORAGE_KEY);
      }
    }
  }

  /**
   * Gibt reaktive Config für einen Block zurück
   */
  getConfig$(blockId: string): LiveData<AutomationConfig> {
    let config$ = this.configs.get(blockId);
    if (!config$) {
      config$ = new LiveData<AutomationConfig>({ ...defaultConfig });
      this.configs.set(blockId, config$);
    }
    return config$;
  }

  /**
   * Aktualisiert die Config eines Blocks
   */
  updateConfig(blockId: string, updates: Partial<AutomationConfig>): void {
    const config$ = this.getConfig$(blockId);
    config$.next({
      ...config$.value,
      ...updates,
    });
  }

  /**
   * Löscht die Config eines Blocks (bei Block-Deletion)
   */
  deleteConfig(blockId: string): void {
    this.configs.delete(blockId);
    if (this.activeBlockId$.value === blockId) {
      this.activeBlockId$.next(null);
    }
  }

  /**
   * sl-67kc.3.4: Publishes a deep-link target for the History panel. The
   * chip deep-link calls this AFTER {@link setActiveBlock} so the target
   * HistoryPanel mounts for the right block before it reads the value.
   */
  setPendingHistoryRowTarget(target: PendingHistoryRowTarget | null): void {
    this.pendingHistoryRowTarget$.next(target);
  }

  /**
   * sl-67kc.3.4: Reads + clears the pending target iff it matches the
   * caller's blockId. Returns the target for the matching block, or
   * `null` when the current target is for a different block or absent.
   *
   * The History panel calls this inside a LiveData effect; by the time
   * it runs, the chip deep-link has already activated the target block
   * and set the target, so a matching read means "this panel owns the
   * scroll — consume it and do not re-fire".
   */
  consumePendingHistoryRowTarget(
    blockId: string
  ): PendingHistoryRowTarget | null {
    const target = this.pendingHistoryRowTarget$.value;
    if (!target || target.blockId !== blockId) return null;
    this.pendingHistoryRowTarget$.next(null);
    return target;
  }
}
